import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Input, Tooltip, message as antdMessage } from 'antd'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  CloseOutlined,
  CustomerServiceOutlined,
  RobotOutlined,
  SendOutlined,
} from '@ant-design/icons'
import { CopilotMessage, getCopilotConfig, sendCopilotMessage } from '../../services/copilot'

type ChatMessage = {
  id: string
  role: 'user' | 'assistant'
  content: string
  status?: 'sending' | 'error'
}

const initialMessage: ChatMessage = {
  id: 'welcome',
  role: 'assistant',
  content: '你好，我是 Kyren Copilot。你可以问我控制台操作、API 对接，或当前账户可用支付方式和费率。',
}

export default function CopilotWidget() {
  const [enabled, setEnabled] = useState(false)
  const [open, setOpen] = useState(false)
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([initialMessage])
  const listRef = useRef<HTMLDivElement>(null)

  const conversation = useMemo(
    () => messages
      .filter((item): item is ChatMessage & CopilotMessage =>
        item.id !== 'welcome' && item.role === 'user' && item.status !== 'error'
      )
      .map(({ role, content }) => ({ role, content })),
    [messages]
  )

  useEffect(() => {
    getCopilotConfig()
      .then((res) => setEnabled(res.data.enabled))
      .catch(() => setEnabled(false))
  }, [])

  useEffect(() => {
    if (!open) return
    listRef.current?.scrollTo({
      top: listRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [messages, open])

  const sendMessage = async () => {
    const content = input.trim()
    if (!content || sending) return

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
    }
    setMessages((items) => [...items, userMessage])
    setInput('')
    setSending(true)

    try {
      const latestPayloadMessage: CopilotMessage = { role: 'user', content }
      const payload: CopilotMessage[] = [...conversation, latestPayloadMessage].slice(-10)
      const res = await sendCopilotMessage(
        payload,
        detectLanguage(content)
      )
      const answer: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: res.data.answer,
      }
      setMessages((items) => [...items, answer])
    } catch (error) {
      const fallback: ChatMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        status: 'error',
        content: error instanceof Error ? error.message : 'Copilot 暫時無法回覆，請稍後再試。',
      }
      setMessages((items) => [...items, fallback])
      antdMessage.error('Copilot 暫時無法回覆')
    } finally {
      setSending(false)
    }
  }

  if (!enabled) return null

  return (
    <div className="copilot-widget">
      {open && (
        <section className="copilot-panel" aria-label="Kyren Copilot">
          <header className="copilot-header">
            <div className="copilot-title">
              <RobotOutlined />
              <span>Kyren Copilot</span>
            </div>
            <Tooltip title="Close">
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                onClick={() => setOpen(false)}
                aria-label="Close Copilot"
              />
            </Tooltip>
          </header>

          <div className="copilot-messages" ref={listRef}>
            {messages.map((item) => (
              <div
                key={item.id}
                className={`copilot-message copilot-message-${item.role}${item.status === 'error' ? ' copilot-message-error' : ''}`}
              >
                {item.role === 'assistant' ? (
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      a: ({ node: _node, ...props }) => (
                        <a {...props} target="_blank" rel="noreferrer noopener" />
                      ),
                    }}
                  >
                    {item.content}
                  </ReactMarkdown>
                ) : (
                  item.content
                )}
              </div>
            ))}
            {sending && (
              <div className="copilot-message copilot-message-assistant copilot-message-pending">
                正在整理答案...
              </div>
            )}
          </div>

          <div className="copilot-input">
            <Input.TextArea
              value={input}
              autoSize={{ minRows: 1, maxRows: 4 }}
              maxLength={1000}
              placeholder="Ask about your account, rates, or API integration"
              onChange={(event) => setInput(event.target.value)}
              onPressEnter={(event) => {
                if (!event.shiftKey) {
                  event.preventDefault()
                  void sendMessage()
                }
              }}
              disabled={sending}
            />
            <Tooltip title="Send">
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={() => void sendMessage()}
                loading={sending}
                aria-label="Send Copilot message"
              />
            </Tooltip>
          </div>
        </section>
      )}

      <Tooltip title="Kyren Copilot">
        <Button
          className="copilot-fab"
          type="primary"
          shape="circle"
          icon={<CustomerServiceOutlined />}
          onClick={() => setOpen((value) => !value)}
          aria-label="Open Kyren Copilot"
        />
      </Tooltip>
    </div>
  )
}

function detectLanguage(content: string): 'en' | 'zh' | 'zh-Hant' {
  if (!/[\u4e00-\u9fff]/.test(content)) return 'en'
  if (/[體臺灣粵門請問資開啟關閉應該匯費結嗎]/.test(content)) return 'zh-Hant'
  if (/[体台湾粤请问资料开启关闭应该汇款费率结算吗]/.test(content)) return 'zh'
  return 'zh'
}
